import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useProjectSetupStore } from "@/zapi-frontend/stores/projectSetup";
import { useChapterMemoStore } from "@/zapi-frontend/stores/chapterMemo";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useFeatureFlag } from "@/zapi-frontend/shared/hooks/useFeatureFlag";
import { useModelLedger } from "../../ModelLedgerIsland/useModelLedger";
import { useRqaSimTwin } from "../../RqaSimIsland/useRqaSim";
import type { TNavEntry } from "../model/types";
import { groupProjectChapters } from "../lib/groupProjectChapters";
import { buildProjectNav, getActiveNavId, getNavSetupState } from "../lib/projectNav";
import { useProjectViewNavigation } from "./useProjectViewNavigation";

export function useProjectNav(projectId: string) {
  const { t } = useTranslation("projectHome");
  const [searchParams] = useSearchParams();
  const { projectChapters, activeChapter, viewMode } = useChapterStore();
  const { selectedProject } = useProjectStore();
  const { isLabs } = useMeStore();
  const { navigate } = useProjectViewNavigation();
  const rqaSimEnabled = useFeatureFlag("rqa-sim", selectedProject?.organizationId) === true;
  const { data: modelLedger } = useModelLedger(projectId);
  const { data: rqaSimTwin } = useRqaSimTwin(rqaSimEnabled ? projectId : "");
  const isSetupActive = useProjectSetupStore((s) => !!s.activeSetupTasks[projectId]);
  const isPolling = useProjectSetupStore((s) => !!s.pollingProjects[projectId]);
  const chapterMemoStatus = useChapterMemoStore((s) => s.chaptersByProject[projectId]);
  const allChapterMemosReady =
    !!chapterMemoStatus && chapterMemoStatus.length > 0 && chapterMemoStatus.every((c) => c.hasActiveMemo);

  const isTemplateLess = selectedProject?.projectTemplateSlug === "";
  const chapterGroups = useMemo(() => groupProjectChapters(projectChapters), [projectChapters]);
  const { showSkeleton, isSetupBlocking } = getNavSetupState({
    isLabs: !!isLabs,
    isDev: import.meta.env.DEV,
    debugGenState: searchParams.get("debugGenState"),
    isSetupActive,
    isPolling,
    isTemplateLess,
    chapterCount: projectChapters.length
  });

  const groups = buildProjectNav({
    groups: chapterGroups,
    isTemplateLess,
    isLabs: !!isLabs,
    showSkeleton,
    isSetupBlocking,
    allChapterMemosReady,
    rqaSimEnabled,
    hasRqaSim: rqaSimEnabled && !!rqaSimTwin,
    hasModelLedger: !!modelLedger,
    t
  });
  const activeId = getActiveNavId({
    groups: chapterGroups,
    viewMode,
    activeChapterId: activeChapter?.id,
    isPresentationOpen: searchParams.get("tab") === "presentation"
  });

  const select = useCallback(
    (entry: TNavEntry) => {
      if (!entry.target) return;
      navigate(entry.target);
    },
    [navigate]
  );

  return { groups, activeId, select };
}
